/**
 * Build information helper
 *
 * Provides environment, build ID, and build timestamp information.
 * This module is completely independent and does not import any context or hooks.
 */

export type AppEnvironment = 'development' | 'staging' | 'production'

export interface BuildInfo {
  env: AppEnvironment
  buildId: string | null
  builtAt: string | null
}

/**
 * Normalizes environment string to AppEnvironment type
 */
function normalizeEnvironment(rawEnv: string | undefined): AppEnvironment {
  if (!rawEnv) return 'development'

  const normalized = rawEnv.toLowerCase().trim()

  // Handle common variations
  if (normalized === 'prod' || normalized === 'production') return 'production'
  if (normalized === 'staging' || normalized === 'stage') return 'staging'
  if (normalized === 'dev' || normalized === 'development') return 'development'

  // Default to development for unknown values
  return 'development'
}

/**
 * Gets build information from environment variables
 *
 * Rules:
 * - env: derived from VITE_APP_ENV (explicit config, not tenant data)
 * - buildId: from VITE_BUILD_ID or __BUILD_ID__ global
 * - builtAt: from VITE_BUILD_TIME or __BUILD_TIME__ global
 */
export function getBuildInfo(): BuildInfo {
  // Environment detection - explicit only, no hostname guessing
  const rawEnv = import.meta.env.VITE_APP_ENV
  const env = normalizeEnvironment(rawEnv)

  // Build ID - prefer env var, fallback to global
  const buildId =
    import.meta.env.VITE_BUILD_ID ||
    (typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : null)

  // Build timestamp - prefer env var, fallback to global
  const builtAt =
    import.meta.env.VITE_BUILD_TIME ||
    (typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : null)

  return {
    env,
    buildId,
    builtAt
  }
}

/**
 * Formats build timestamp to human-readable format
 */
export function formatBuildTime(timestamp: string | null): string {
  if (!timestamp) return 'Not available'

  try {
    const date = new Date(timestamp)
    if (isNaN(date.getTime())) return 'Invalid date'

    // Format as: 2025-12-11 12:00 UTC
    return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC')
  } catch {
    return 'Invalid date'
  }
}

/**
 * Gets environment badge color
 */
export function getEnvironmentColor(env: AppEnvironment): string {
  switch (env) {
    case 'production':
      return 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/30'
    case 'staging':
      return 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30'
    case 'development':
      return 'text-[#1F2A44] dark:text-[#1F2A44] bg-[rgba(31,42,68,0.06)] dark:bg-[rgba(31,42,68,0.1)]'
  }
}

/**
 * Gets display name for environment
 */
export function getEnvironmentDisplay(env: AppEnvironment): string {
  switch (env) {
    case 'production':
      return 'Production'
    case 'staging':
      return 'Staging'
    case 'development':
      return 'Development'
  }
}
